import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getCustomerAccounts } from 'app/entities/customer-account/customer-account.reducer';
import { getEntities as getOrganisations } from 'app/entities/organisation/organisation.reducer';
import { getEntities as getAddresses } from 'app/entities/address/address.reducer';
import { getEntities as getContacts } from 'app/entities/contact/contact.reducer';
import { OrganizationType } from 'app/shared/model/enumerations/organization-type.model';
import { EnterpriseSize } from 'app/shared/model/enumerations/enterprise-size.model';
import { createEntity, getEntity, reset, updateEntity } from './organisation.reducer';

export const OrganisationUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const customerAccounts = useAppSelector(state => state.customerAccount.entities);
  const organisations = useAppSelector(state => state.organisation.entities);
  const addresses = useAppSelector(state => state.address.entities);
  const contacts = useAppSelector(state => state.contact.entities);
  const organisationEntity = useAppSelector(state => state.organisation.entity);
  const loading = useAppSelector(state => state.organisation.loading);
  const updating = useAppSelector(state => state.organisation.updating);
  const updateSuccess = useAppSelector(state => state.organisation.updateSuccess);
  const organizationTypeValues = Object.keys(OrganizationType);
  const enterpriseSizeValues = Object.keys(EnterpriseSize);

  const handleClose = () => {
    navigate('/organisation');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getCustomerAccounts({}));
    dispatch(getOrganisations({}));
    dispatch(getAddresses({}));
    dispatch(getContacts({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    const entity = {
      ...organisationEntity,
      ...values,
      customerAccount: customerAccounts.find(it => it.id.toString() === values.customerAccount?.toString()),
      parentOrganisation: organisations.find(it => it.id.toString() === values.parentOrganisation?.toString()),
      organisationAddress: addresses.find(it => it.id.toString() === values.organisationAddress?.toString()),
      contact: contacts.find(it => it.id.toString() === values.contact?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          organizationType: 'ZZP',
          enterpriseSize: 'MICRO',
          ...organisationEntity,
          customerAccount: organisationEntity?.customerAccount?.id,
          parentOrganisation: organisationEntity?.parentOrganisation?.id,
          organisationAddress: organisationEntity?.organisationAddress?.id,
          contact: organisationEntity?.contact?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="crawlerBackendApp.organisation.home.createOrEditLabel" data-cy="OrganisationCreateUpdateHeading">
            <Translate contentKey="crawlerBackendApp.organisation.home.createOrEditLabel">
              Create or edit a Organisation
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="organisation-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.organisation.organisationId')}
                id="organisation-organisationId"
                name="organisationId"
                data-cy="organisationId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.organisation.name')}
                id="organisation-name"
                name="name"
                data-cy="name"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.organisation.vatId')}
                id="organisation-vatId"
                name="vatId"
                data-cy="vatId"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.organisation.organizationType')}
                id="organisation-organizationType"
                name="organizationType"
                data-cy="organizationType"
                type="select"
              >
                {organizationTypeValues.map(organizationType => (
                  <option value={organizationType} key={organizationType}>
                    {translate(`crawlerBackendApp.OrganizationType.${organizationType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.organisation.status')}
                id="organisation-status"
                name="status"
                data-cy="status"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.organisation.enterpriseSize')}
                id="organisation-enterpriseSize"
                name="enterpriseSize"
                data-cy="enterpriseSize"
                type="select"
              >
                {enterpriseSizeValues.map(enterpriseSize => (
                  <option value={enterpriseSize} key={enterpriseSize}>
                    {translate(`crawlerBackendApp.EnterpriseSize.${enterpriseSize}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                id="organisation-customerAccount"
                name="customerAccount"
                data-cy="customerAccount"
                label={translate('crawlerBackendApp.organisation.customerAccount')}
                type="select"
              >
                <option value="" key="0" />
                {customerAccounts
                  ? customerAccounts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="organisation-parentOrganisation"
                name="parentOrganisation"
                data-cy="parentOrganisation"
                label={translate('crawlerBackendApp.organisation.parentOrganisation')}
                type="select"
              >
                <option value="" key="0" />
                {organisations
                  ? organisations.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="organisation-organisationAddress"
                name="organisationAddress"
                data-cy="organisationAddress"
                label={translate('crawlerBackendApp.organisation.organisationAddress')}
                type="select"
              >
                <option value="" key="0" />
                {addresses
                  ? addresses.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="organisation-contact"
                name="contact"
                data-cy="contact"
                label={translate('crawlerBackendApp.organisation.contact')}
                type="select"
              >
                <option value="" key="0" />
                {contacts
                  ? contacts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/organisation"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default OrganisationUpdate;
